import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Loader2, MessageSquare } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/ui/dialog";
import { QueryErrorBanner } from "@/shared/ui/QueryErrorBanner";
import { coachingService } from "../../api/coachingService";

type Props = {
  evalId: number | null;
  onClose: () => void;
};

/** 显示对话记录(transcript turns) — 替代原来的考核结果详情。考核已经在「评估考核」tab 看,此处只看对话。 */
export function TranscriptDialog({ evalId, onClose }: Props) {
  const { t } = useTranslation("coaching");
  const {
    data: detail,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["coaching-eval-detail", evalId],
    queryFn: () => coachingService.getEvaluationDetail(evalId!),
    enabled: evalId !== null,
    meta: { suppressGlobalErrorToast: true },
  });

  return (
    <Dialog open={evalId !== null} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-primary" />
            {t("transcript.title")}
            {detail && (
              <span className="ml-2 text-xs font-normal normal-case tracking-normal text-muted-foreground">
                {detail.employeeName} · {detail.scenarioName ?? "—"}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {isError ? (
          // audit-0713 C1c:出错不再永久转圈
          <QueryErrorBanner isError className="my-6" onRetry={() => void refetch()} />
        ) : isLoading || !detail ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (detail.transcript ?? []).length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            {t("transcript.empty")}
            <p className="mt-1 text-xs">{t("transcript.emptyHint")}</p>
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto space-y-3 pr-2">
            {(detail.transcript ?? []).map((turn, idx) => {
              // backend v45 (2026-05-19) 把 LLM role 重映射成业务 role:
              //   user → employee / assistant → customer。老数据可能仍是 user/assistant。
              const isEmployee = turn.role === "employee" || turn.role === "user";
              const isCustomer = !isEmployee;
              return (
                <div
                  key={idx}
                  className={`flex gap-2 fx-enter ${isCustomer ? "justify-start" : "justify-end"}`}
                  style={{ animationDelay: `${Math.min(idx, 7) * 30}ms` }}
                >
                  {isCustomer && (
                    <div className="w-8 h-8 rounded-full bg-info text-info-foreground text-2xs font-semibold flex items-center justify-center shrink-0">
                      {t("transcript.customer")}
                    </div>
                  )}
                  <div
                    className={`max-w-[75%] px-3 py-2 rounded-xl text-sm leading-relaxed whitespace-pre-wrap ${
                      isCustomer
                        ? "bg-muted text-foreground rounded-tl-sm"
                        : "bg-primary text-primary-foreground rounded-tr-sm"
                    }`}
                  >
                    {turn.content}
                  </div>
                  {!isCustomer && (
                    <div className="w-8 h-8 rounded-full bg-success text-success-foreground text-2xs font-semibold flex items-center justify-center shrink-0">
                      {t("transcript.employee")}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
